import React, { createContext, useContext } from 'react';
import { useData } from './DataContext';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

// Define comprehensive color palettes
export const lightTheme = {
  // Main backgrounds
  background: '#f5f5f5',
  backgroundSecondary: '#ffffff',
  
  // Card/Container backgrounds
  cardBackground: '#ffffff',
  cardBackgroundHover: '#f8f9fa',
  
  // Text colors
  text: '#2c3e50',
  textSecondary: '#6c757d',
  textMuted: '#adb5bd',
  textPrimary: '#2c3e50',
  
  // Borders - WCAG AA compliant (3:1 contrast ratio minimum)
  border: '#adb5bd',
  borderLight: '#ced4da',
  
  // Input backgrounds
  inputBackground: '#ffffff',
  inputBackgroundFocus: '#f8f9fa',
  inputBorder: '#adb5bd',
  inputBorderFocus: '#5865f2',
  
  // Navigation
  navBackground: '#ffffff',
  navBorder: '#adb5bd',
  navText: '#2c3e50',
  navTextHover: '#5865f2',
  
  // Buttons
  buttonPrimary: '#5865f2',
  buttonPrimaryHover: '#4752c4',
  buttonSecondary: '#6c757d',
  buttonSecondaryHover: '#5a6268',
  buttonDanger: '#dc3545',
  buttonDangerHover: '#c82333',
  buttonSuccess: '#28a745',
  buttonSuccessHover: '#218838',
  
  // Status colors
  statusPending: '#ffc107',
  statusInProgress: '#17a2b8',
  statusCompleted: '#28a745',
  statusOverdue: '#dc3545',
  
  // Shadows
  shadow: 'rgba(0, 0, 0, 0.1)',
  shadowMedium: 'rgba(0, 0, 0, 0.15)',
  shadowLarge: 'rgba(0, 0, 0, 0.2)',
  
  // Special elements
  scrollbarThumb: '#cbd5e0',
  scrollbarThumbHover: '#a0aec0',
  scrollbarTrack: '#f7fafc',
  
  // Calendar
  calendarToday: '#e3f2fd',
  calendarSelected: '#5865f2',
  calendarHover: '#f1f3f5',
  
  // Charts
  chartPrimary: '#5865f2',
  chartSecondary: '#17a2b8',
  chartTertiary: '#28a745',
  
  // Brand colors
  primary: '#5865f2',
  surface: '#ffffff',
  hoverBackground: '#f8f9fa',
};

export const darkTheme = {
  // Main backgrounds
  background: '#36393f',
  backgroundSecondary: '#2f3136',
  
  // Card/Container backgrounds
  cardBackground: '#40444b',
  cardBackgroundHover: '#4f545c',
  
  // Text colors
  text: '#ffffff',
  textSecondary: '#b9bbbe',
  textMuted: '#72767d',
  textPrimary: '#ffffff',
  
  // Borders - WCAG AA compliant (3:1 contrast ratio minimum)
  border: '#6c757d',
  borderLight: '#5a6268',
  
  // Input backgrounds
  inputBackground: '#2f3136',
  inputBackgroundFocus: '#36393f',
  inputBorder: '#6c757d',
  inputBorderFocus: '#5865f2',
  
  // Navigation
  navBackground: '#2f3136',
  navBorder: '#6c757d',
  navText: '#ffffff',
  navTextHover: '#5865f2',
  
  // Buttons
  buttonPrimary: '#5865f2',
  buttonPrimaryHover: '#4752c4',
  buttonSecondary: '#4f545c',
  buttonSecondaryHover: '#5a5f6a',
  buttonDanger: '#dc3545',
  buttonDangerHover: '#c82333',
  buttonSuccess: '#28a745',
  buttonSuccessHover: '#218838',
  
  // Status colors
  statusPending: '#fbc02d',
  statusInProgress: '#3498db',
  statusCompleted: '#2ecc71',
  statusOverdue: '#e74c3c',
  
  // Shadows
  shadow: 'rgba(0, 0, 0, 0.2)',
  shadowMedium: 'rgba(0, 0, 0, 0.3)',
  shadowLarge: 'rgba(0, 0, 0, 0.4)',
  
  // Special elements
  scrollbarThumb: '#202225',
  scrollbarThumbHover: '#2f3136',
  scrollbarTrack: '#2f3136',
  
  // Calendar
  calendarToday: '#5865f2',
  calendarSelected: '#5865f2',
  calendarHover: '#4f545c',
  
  // Charts
  chartPrimary: '#5865f2',
  chartSecondary: '#3498db',
  chartTertiary: '#2ecc71',
  
  // Brand colors
  primary: '#5865f2',
  surface: '#2f3136',
  hoverBackground: '#4f545c',
};

export const ThemeProvider = ({ children }) => {
  const { settings, updateSettings, loading } = useData();

  // Wait for settings to load before rendering children to prevent theme flash
  if (loading) {
    // Optionally, return a minimal loading indicator or null
    return null;
  }

  const themePreference = settings?.theme || 'light';
  const isDark = themePreference === 'dark' || themePreference === 'discord';
  const currentTheme = isDark ? darkTheme : lightTheme;
  const themeMode = isDark ? 'dark' : 'light';

  const toggleTheme = async () => {
    const newTheme = isDark ? 'light' : 'dark';
    await updateSettings({ theme: newTheme });
  };

  const value = {
    theme: currentTheme,
    themeMode,
    toggleTheme,
    isDark,
    isLight: !isDark
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
